import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, FlaskConical, FileCheck, Truck, Mail } from 'lucide-react';
import SEO from '../components/SEO';

// Single source of truth for the current pre-order wave end date.
const PREORDER_END_DATE = 'June 30, 2026';

export default function PreOrderPolicyPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="pre-order-policy-page">
      <SEO
        title="Pre-Order Policy"
        description={`Wilvada LC pre-order policy. Current wave is open until ${PREORDER_END_DATE}. Orders will begin processing after the wave closes, with COAs uploaded prior to shipment.`}
      />

      <div className="mb-10">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[hsl(210,45%,40%)] mb-3 font-body" data-testid="pre-order-policy-eyebrow">
          Pre-Order Wave 01 — Open
        </p>
        <h1 className="text-4xl sm:text-5xl tracking-tight font-light text-foreground font-heading mb-3" data-testid="pre-order-policy-heading">
          Pre-Order Policy
        </h1>
        <p className="text-sm text-muted-foreground font-body">
          Last Updated: April 2026
        </p>
      </div>

      <div className="rounded-xl border border-[hsl(210,45%,40%)]/30 bg-[hsl(210,45%,40%)]/[0.06] p-6 mb-10" data-testid="pre-order-policy-callout">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-0.5">
            <CalendarClock className="h-6 w-6 text-[hsl(210,45%,40%)]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[hsl(210,52%,21%)] font-heading mb-1">
              Current wave closes <span className="underline decoration-2 underline-offset-2">{PREORDER_END_DATE}</span>
            </p>
            <p className="text-sm text-muted-foreground font-body leading-relaxed">
              We are accepting pre-orders now to gauge demand and finalize our launch inventory. Orders placed before the wave closes are queued; no order is processed and no card is charged for fulfillment until after the cutoff.
            </p>
          </div>
        </div>
      </div>

      <div className="prose prose-sm max-w-none text-muted-foreground font-body space-y-5 leading-relaxed">
        <p>
          Wilvada LC is currently operating in pre-launch. Every product offered on this site is being made available
          for pre-order so we can lock in synthesis quantities, QA timelines, and shipping partners ahead of our first
          public release. Please read this policy in full before placing a pre-order — it explains exactly when your
          order will be processed, when COAs will be issued, and what happens if anything changes.
        </p>

        <h2 className="text-lg font-heading font-semibold text-foreground mt-8 mb-2">1. What is a pre-order?</h2>
        <p>
          A pre-order is a binding commitment to purchase a research peptide or piece of laboratory equipment that
          has not yet entered the fulfillment queue. Your card is authorized at checkout and the charge is captured
          to reserve your spot, but the order itself stays in <strong className="text-foreground">"Queued"</strong> status
          until after the wave closes on <strong className="text-foreground">{PREORDER_END_DATE}</strong>.
        </p>

        <h2 className="text-lg font-heading font-semibold text-foreground mt-8 mb-2">2. Wave 01 timeline</h2>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong className="text-foreground">Now → {PREORDER_END_DATE}:</strong> Pre-order window. Place your order any time during this window.</li>
          <li><strong className="text-foreground">July 1, 2026:</strong> Wave 01 closes. We begin batch synthesis QA and prepare orders for the fulfillment queue.</li>
          <li><strong className="text-foreground">~7-14 days post-close:</strong> Certificates of Analysis (HPLC, mass spec, peptide content) are uploaded to your account dashboard for every line item.</li>
          <li><strong className="text-foreground">Within 24 hours of COA upload:</strong> Your order ships, and you receive an emailed tracking number from our carrier.</li>
        </ul>

        <h2 className="text-lg font-heading font-semibold text-foreground mt-8 mb-2">3. Certificates of Analysis (COAs)</h2>
        <p>
          Every Wilvada batch is third-party tested. COAs include the production lot ID, HPLC purity (≥99% target),
          peptide content by mass, mass-spectrometry confirmation, and the date of analysis. <strong className="text-foreground">No order
          ships without a COA attached to each line item.</strong> COAs are made available in your account dashboard the moment
          they are issued, and a download link is included in your shipment notification email.
        </p>

        <h2 className="text-lg font-heading font-semibold text-foreground mt-8 mb-2">4. Pricing &amp; payment</h2>
        <p>
          Prices displayed at the time of pre-order are locked in. If our launch prices change after the wave closes,
          pre-order customers are billed at the lower of the two — never the higher. Standard promo codes and the
          existing free-shipping thresholds ($100 standard, $175 2-day) apply at pre-order checkout.
        </p>
        <p>
          Your card is charged at checkout, not at fulfillment. This is how we secure inventory allocations from our
          synthesis partners and how we keep the wave open without overcommitting capacity.
        </p>

        <h2 className="text-lg font-heading font-semibold text-foreground mt-8 mb-2">5. Cancellations &amp; refunds</h2>
        <p>
          A pre-order can be cancelled for a full refund at any time before the wave closes on {PREORDER_END_DATE} — simply
          email <a href="mailto:support@wilvada.com" className="text-[hsl(210,45%,40%)] hover:underline">support@wilvada.com</a> from
          the address on your order, or use the "Request Cancellation" button in your account dashboard. Refunds post back to the original
          payment method within 5-10 business days.
        </p>
        <p>
          After {PREORDER_END_DATE}, standard refund policy applies — see our{' '}
          <Link to="/refunds" className="text-[hsl(210,45%,40%)] hover:underline">Refunds &amp; Returns policy</Link>{' '}
          for the full terms.
        </p>

        <h2 className="text-lg font-heading font-semibold text-foreground mt-8 mb-2">6. Delays, substitutions &amp; communication</h2>
        <p>
          In the rare event a batch fails QA or our synthesis partner reports a scheduling delay, we'll email every
          affected pre-order customer within 24 hours of receiving the notice — with two options: (a) wait for the
          replacement batch (typically +1-2 weeks) at no additional cost, or (b) cancel that line item for a full refund.
        </p>
        <p>
          We will <strong className="text-foreground">never</strong> ship a substitute peptide or a different concentration without
          your explicit written approval.
        </p>

        <h2 className="text-lg font-heading font-semibold text-foreground mt-8 mb-2">7. Research-use only — pre-order included</h2>
        <p>
          Pre-ordered materials are subject to the same{' '}
          <Link to="/terms" className="text-[hsl(210,45%,40%)] hover:underline">Terms &amp; Conditions</Link>{' '}
          as all Wilvada products. They are sold strictly for <strong className="text-foreground">in-vitro and pre-clinical
          research</strong> by qualified professionals. They are not for human or veterinary use, and they are not pharmaceutical
          products. Age verification (21+) and acknowledgment of research-use-only intent are required at checkout for every
          pre-order.
        </p>

        <h2 className="text-lg font-heading font-semibold text-foreground mt-8 mb-2">8. Questions</h2>
        <p>
          We're a small, US-based research-supply company and we read every email. If anything in this policy is unclear,
          or you'd like a timeline update on a specific batch, write to{' '}
          <a href="mailto:support@wilvada.com" className="text-[hsl(210,45%,40%)] hover:underline">support@wilvada.com</a>{' '}
          or use the <Link to="/contact" className="text-[hsl(210,45%,40%)] hover:underline">contact form</Link>. Typical reply
          time is under 24 hours, M-F.
        </p>
      </div>

      <div className="mt-12 grid sm:grid-cols-2 gap-4" data-testid="pre-order-policy-tldr">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-5">
          <FlaskConical className="h-5 w-5 text-[hsl(210,45%,40%)] mb-2" />
          <p className="text-sm font-semibold text-slate-900 font-heading mb-1">Reserve your batch</p>
          <p className="text-xs text-slate-600 font-body leading-relaxed">Pre-orders lock in your spot in Wave 01 and your launch-day pricing.</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-5">
          <FileCheck className="h-5 w-5 text-emerald-600 mb-2" />
          <p className="text-sm font-semibold text-slate-900 font-heading mb-1">COAs before shipment</p>
          <p className="text-xs text-slate-600 font-body leading-relaxed">Every line item gets an HPLC + mass-spec COA in your dashboard before it ships.</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-5">
          <Truck className="h-5 w-5 text-[hsl(210,45%,40%)] mb-2" />
          <p className="text-sm font-semibold text-slate-900 font-heading mb-1">Ships ~July 2026</p>
          <p className="text-xs text-slate-600 font-body leading-relaxed">Wave 01 enters fulfillment July 1 — most orders ship within 7-14 days after COAs are issued.</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-5">
          <Mail className="h-5 w-5 text-emerald-600 mb-2" />
          <p className="text-sm font-semibold text-slate-900 font-heading mb-1">Cancel any time</p>
          <p className="text-xs text-slate-600 font-body leading-relaxed">Full refund any time before {PREORDER_END_DATE}, no questions asked.</p>
        </div>
      </div>
    </div>
  );
}
